import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useState } from "react";
import { useI18n } from "@/lib/i18n";
import { breadcrumbSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { ConsultCta } from "@/components/consult-cta";

export const Route = createFileRoute("/rechner")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => ({
    ...localizedHead("/rechner", loaderData?.lang),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/rechner", loaderData?.lang)) },
      { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/rechner", loaderData?.lang, { specialty: "Endocrine", aspect: "Prognosis" })) },
    ],
  }),
  component: Page,
});



const drugs = [
  { name: "Semaglutid", pct: 0.15, color: "bg-indigo/60", label: "Gen 1" },
  { name: "Tirzepatid", pct: 0.22, color: "bg-indigo", label: "Gen 2" },
  { name: "Retatrutid", pct: 0.24, color: "bg-emerald", label: "Gen 3" },
];

function Page() {
  const { t } = useI18n();
  const [weight, setWeight] = useState(100);
  const maxLoss = Math.max(...drugs.map((d) => weight * d.pct));

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 md:py-20">
      <div className="max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {t.calc.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {t.calc.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{t.calc.lead}</p>
      </div>

      <div className="mt-10 card-elevated p-6 md:p-10">
        <div className="flex items-baseline justify-between">
          <label htmlFor="weight" className="text-sm font-medium text-muted-foreground">
            {t.calc.weight}
          </label>
          <span className="font-display text-4xl text-foreground md:text-5xl">
            {weight}
            <span className="text-xl text-muted-foreground"> {t.calc.kg}</span>
          </span>
        </div>
        <input
          id="weight"
          type="range"
          min={60}
          max={180}
          step={1}
          value={weight}
          onChange={(e) => setWeight(Number(e.target.value))}
          className="mt-4 w-full accent-indigo"
        />
        <div className="mt-2 flex justify-between text-xs text-muted-foreground">
          <span>60 {t.calc.kg}</span>
          <span>180 {t.calc.kg}</span>
        </div>

        <div className="mt-10 space-y-6">
          {drugs.map((d) => {
            const loss = weight * d.pct;
            const target = weight - loss;
            const barPct = (loss / maxLoss) * 100;
            return (
              <div key={d.name}>
                <div className="flex items-baseline justify-between">
                  <div>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {d.label}
                    </span>
                    <p className="text-lg font-semibold text-foreground">{d.name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-3xl text-foreground md:text-4xl">
                      -{loss.toFixed(1)}
                      <span className="text-base text-muted-foreground"> {t.calc.kg}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.calc.target} {target.toFixed(1)} {t.calc.kg}
                    </p>
                  </div>
                </div>
                <div className="mt-3 h-3 overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className={`h-full rounded-full ${d.color}`}
                    initial={false}
                    animate={{ width: `${barPct}%` }}
                    transition={{ type: "spring", stiffness: 120, damping: 18 }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-10 rounded-xl bg-muted/50 p-4 text-xs leading-relaxed text-muted-foreground">
          <strong className="text-foreground">{t.calc.note}</strong> {t.calc.disclaimer}
        </p>

        <ConsultCta variant="section" />
      </div>
    </div>
  );
}
